import { useHandler } from "@redotech/react-util/hook";
import {
  BrandKit,
  defaultBrandKit,
} from "@redotech/redo-content-builder-common/brand-kit";
import {
  CloseAction,
  ElementVisibility,
  MarketingForgeElementType,
} from "@redotech/redo-content-builder-common/element-schema";
import { LayoutType } from "@redotech/redo-marketing-common/signup-form/signup-form";
import {
  DesktopStepConfig,
  MobileStepConfig,
  SpotlightForm,
  stepBackgroundStyles,
} from "@redotech/redo-spotlight-common/spotlight-schema";
import {
  ButtonSize,
  RedoButtonCloseX,
} from "@redotech/redo-web/arbiter-components/redo-button-close-x";
import { Flex } from "@redotech/redo-web/flex";
import { memo, NamedExoticComponent, useMemo, useRef, useState } from "react";
import { useCloseButtonOnScreen } from "../close-button-position";
import { SignupFormDiscountCodeDisplay } from "../form-elements/discount-code";
import { FormButton } from "../form-elements/form-button";
import { FormColumn } from "../form-elements/form-column";
import { FormContainer } from "../form-elements/form-container";
import { FormImage } from "../form-elements/form-image";
import { FormProduct } from "../form-elements/form-product";
import { FormElementProps } from "../form-elements/index";
import { Input } from "../form-elements/input";
import { RichText } from "../form-elements/rich-text";
import { TextToSubscribeButton } from "../form-elements/text-to-subscribe-button";
import { TextToSubscribeDescription } from "../form-elements/text-to-subscribe-description";
import { executeCloseActions } from "./close-action-executor";
import { SpotlightFontLoader } from "./spotlight-font-loader";

const elementComponents: Record<
  MarketingForgeElementType,
  NamedExoticComponent<FormElementProps<any>>
> = {
  [MarketingForgeElementType.INPUT]: Input,
  [MarketingForgeElementType.RICH_TEXT]: RichText,
  [MarketingForgeElementType.BUTTON]: FormButton,
  [MarketingForgeElementType.IMAGE]: FormImage,
  [MarketingForgeElementType.PRODUCT]: FormProduct,
  [MarketingForgeElementType.COLUMN]: FormColumn,
  [MarketingForgeElementType.CONTAINER]: FormContainer,
  [MarketingForgeElementType.DISCOUNT]: SignupFormDiscountCodeDisplay,
  [MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON]: TextToSubscribeButton,
  [MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION]:
    TextToSubscribeDescription,
};

export interface SpotlightFormContentProps {
  spotlight: SpotlightForm;
  currentStep: number;
  brandKit?: BrandKit;
  anonymousId?: string;
  merchantPhoneNumber?: string;
  onClose?: () => void;
  isMobile: boolean;
  config: MobileStepConfig | DesktopStepConfig;
  onSubmitStep: (
    stepIndex: number,
    formData: Record<string, string>,
  ) => Promise<{ resolvedDiscounts?: Record<string, string> } | void>;
  resolvedDiscounts?: Record<string, string>;
  beforePossibleCheckoutNavigation?: () => Promise<void>;
  buttonSubtext?: string;
}

export const SpotlightFormContent = memo(function SpotlightFormContent({
  spotlight,
  brandKit,
  currentStep,
  anonymousId,
  merchantPhoneNumber,
  onClose,
  config,
  isMobile,
  onSubmitStep,
  resolvedDiscounts,
  beforePossibleCheckoutNavigation,
  buttonSubtext,
}: SpotlightFormContentProps) {
  const [submitting, setSubmitting] = useState(false);
  const step = spotlight.steps[currentStep];

  const closeActionContext = useMemo(
    () => ({
      resolvedDiscounts,
      beforePossibleCheckoutNavigation,
      buttonSubtext,
    }),
    [resolvedDiscounts, beforePossibleCheckoutNavigation, buttonSubtext],
  );

  // Holds CLOSE_AND_ACTION actions registered by a submit-button's click
  // handler. The form's onSubmit reads this after awaiting the submit so
  // close actions run with the freshly-resolved discount, not the stale
  // (likely empty) snapshot the click handler would have seen.
  const pendingCloseActionsRef = useRef<CloseAction[] | null>(null);
  const registerPendingCloseActions = useHandler((actions: CloseAction[]) => {
    pendingCloseActionsRef.current = actions;
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLDivElement>(null);
  useCloseButtonOnScreen(containerRef, closeButtonRef);

  if (!step) {
    return null;
  }

  const advancedSettings = config.advancedSettings;
  const backgroundColor = advancedSettings.background;
  const backgroundStyles = stepBackgroundStyles(advancedSettings);
  const padding = advancedSettings.padding;
  const cornerRadius =
    "cornerRadius" in advancedSettings ? advancedSettings.cornerRadius : 0;

  return (
    <Flex
      dir="column"
      gap="none"
      grow="1"
      ref={containerRef}
      style={{
        ...backgroundStyles,
        padding: `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`,
        position: "relative",
        borderRadius: cornerRadius,
        overflow: "hidden",
      }}
    >
      <SpotlightFontLoader
        customFontFamilies={brandKit?.customFontFamilies}
        spotlight={spotlight}
      />
      {onClose && (
        <Flex
          ref={closeButtonRef}
          style={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}
        >
          <RedoButtonCloseX
            onclick={onClose}
            onDarkBackground={false}
            size={ButtonSize.SMALL}
            style={{
              color: spotlight.closeButton.color,
              background: spotlight.closeButton.background.enabled
                ? spotlight.closeButton.background.color
                : "transparent",
            }}
          />
        </Flex>
      )}
      <form
        onSubmit={async (e) => {
          setSubmitting(true);
          e.preventDefault();
          const formData = new FormData(e.currentTarget);
          const transformedData: Record<string, string> = {};
          for (const [key, value] of formData.entries()) {
            transformedData[key] = value as string;
          }
          try {
            const result = await onSubmitStep(currentStep, transformedData);

            const pendingActions = pendingCloseActionsRef.current;
            if (pendingActions) {
              await executeCloseActions(pendingActions, {
                ...closeActionContext,
                resolvedDiscounts: {
                  ...closeActionContext.resolvedDiscounts,
                  ...result?.resolvedDiscounts,
                },
              });
              onClose?.();
            }
          } catch (err) {
            console.error(err);
          } finally {
            pendingCloseActionsRef.current = null;
            setSubmitting(false);
          }
        }}
        style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
      >
        {step.elements
          .filter((element) => {
            const visibility = element.visibility ?? ElementVisibility.ALL;
            if (visibility === ElementVisibility.DESKTOP_ONLY && isMobile) {
              return false;
            }
            if (visibility === ElementVisibility.MOBILE_ONLY && !isMobile) {
              return false;
            }
            return true;
          })
          .map((element, index) => {
            const ElementComponent = elementComponents[element.type];
            return (
              <ElementComponent
                anonymousId={anonymousId}
                backgroundColor={backgroundColor}
                brandKit={brandKit ?? defaultBrandKit}
                brandKitOverrides={undefined}
                closeActionContext={closeActionContext}
                element={element}
                isMobile={isMobile}
                key={index}
                layoutType={LayoutType.POPUP}
                merchantPhoneNumber={merchantPhoneNumber}
                onClose={onClose}
                registerPendingCloseActions={registerPendingCloseActions}
                submitting={submitting}
              />
            );
          })}
      </form>
    </Flex>
  );
});
